/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useSwiper} from 'swiper/react';

import {Box, useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {VIDEO_OPTIONS} from '@amblique/base/app/constants/video-options';
import {optionsResolver} from '@amblique/base/app/helpers/options-resolver';
import {
  useSwiperActiveSlideDomPlay,
  useSwiperAutoplaySuppress,
} from '@amblique/base/app/hooks/use-swiper-video-carousel-autoplay';
import {usePluginOptions} from '@amblique/base/app/hooks/use-plugin-options';
import {useResponsiveValue} from '@amblique/base/app/hooks/use-responsive-value';
import {useVideoAutopilot} from '@amblique/base/app/hooks/use-video-autopilot';
import {useVideoPlayer} from '@amblique/base/app/hooks/use-video-player';

/** True when id is a URL or path; use as-is and skip responsive value parsing. */
function isVideoUrlOrPath(id) {
  return typeof id === 'string' && (
    id.startsWith('http') || id.startsWith('/') || id.startsWith('./') ||
    id.startsWith('data:') || id.startsWith('blob:')
  );
}

/**
 * Video Component
 *
 * Plays Vimeo and YouTube videos with advanced features:
 * - Server-side configuration via site preferences
 * - Responsive video IDs (responsive value format)
 * - Breakpoint-based options
 * - Autopilot (visibility-based play/pause)
 * - Imperative API via ref
 *
 * @param {{
 *   id: string,
 *   presets?: string | string[],
 *   options?: object | string,
 *   ratio?: number,
 *   clip?: number | string | array,
 *   size?: 'cover' | 'contain' | 'fill',
 *   readonly?: boolean,
 *   autopilot?: boolean | string | object,
 *   className?: string,
 * }} props
 *
 * @example Basic usage
 * <Video id="16401445" />
 *
 * @example With presets
 * <Video id="16401445" presets="hero" />
 *
 * @example Readonly mode (auto-enables autopilot for visibility-based playback)
 * <Video id="16401445" readonly />
 *
 * @example With autopilot (auto-applies autopilot preset)
 * <Video id="16401445" autopilot />
 * <Video id="16401445" autopilot="resume" />
 *
 * @example With responsive video IDs (CSS property syntax)
 * <Video id="mobile: mobile-id; tablet: tablet-id; desktop: desktop-id" />
 *
 * @example With responsive video IDs (shorthand syntax)
 * <Video id="mobile-id, tablet-id, desktop-id" />
 *
 * @example With inline options
 * <Video
 *   id="16401445"
 *   presets="readonly"
 *   options={{ loop: false, controls: true }}
 * />
 *
 * @example With ref for imperative control
 * const videoRef = useRef();
 * <Video ref={videoRef} id="16401445" />
 * // Later: videoRef.current.play()
 */
export const Video = forwardRef(({
  id,
  presets,
  options,
  ratio,
  clip,
  size,
  readonly,
  autopilot: autopilotProp,
  className,
  ...props
}, ref) => {
  const styles = useStyleConfig('Video');
  const swiper = useSwiper();
  const suppressAutopilotLeaveRef = useRef(false);

  const {userPresets, userOptions} = useMemo(() => {
    const userPresets = [...(Array.isArray(presets) ? presets : typeof presets === 'string' ? [presets] : [])];
    const userOptions = {
      ...options,
      ...(ratio !== undefined && {ratio}),
      ...(clip !== undefined && {clip}),
      ...(size !== undefined && {size}),
      ...(readonly !== undefined && {readonly}),
      ...(autopilotProp !== undefined && {autopilot: autopilotProp}),
    };

    if (userOptions.readonly && userOptions.autopilot === undefined) {
      userOptions.autopilot = true;
    }

    return {userOptions, userPresets};
  }, [options, ratio, clip, size, readonly, autopilotProp, presets]);

  const readonlyPreset = useMemo(
    () => optionsResolver.resolvePreset(VIDEO_OPTIONS, 'readonly'),
    []
  );
  const autopilotPreset = useMemo(
    () => optionsResolver.resolvePreset(VIDEO_OPTIONS, 'autopilot'),
    []
  );

  const postProcess = useCallback((resolved) => {
    if (resolved.readonly) {
      Object.assign(resolved, readonlyPreset);
    }

    if (resolved.autopilot) {
      Object.assign(resolved, autopilotPreset);
    }

    return resolved;
  }, [autopilotPreset, readonlyPreset]);

  const responsiveOptions = usePluginOptions('video', VIDEO_OPTIONS, userPresets, userOptions, postProcess);
  const responsiveId = useResponsiveValue(id, 'value');
  const currentVideoId = isVideoUrlOrPath(id) ? id : responsiveId;
  const {containerRef, video, init} = useVideoPlayer(currentVideoId, responsiveOptions);

  const autopilotOn = !!responsiveOptions?.autopilot;

  useVideoAutopilot({
    elementRef: containerRef,
    video,
    options: responsiveOptions,
    init,
    suppressLeaveRef: suppressAutopilotLeaveRef,
  });

  useSwiperAutoplaySuppress(swiper, autopilotOn, suppressAutopilotLeaveRef);
  useSwiperActiveSlideDomPlay(swiper, autopilotOn, containerRef, video, init);

  useImperativeHandle(ref, () => video, [video]);

  const isReadonly = responsiveOptions.readonly || readonly;
  const classNames = ['amb-video', isReadonly && 'readonly', className].filter(Boolean).join(' ');

  useEffect(() => {
    if (swiper?.params?.autoHeight) {
      swiper.updateAutoHeight();
    }
  }, [swiper]);

  return (
    <Box
      ref={containerRef}
      aspectRatio={responsiveOptions.ratio || 16 / 9}
      className={classNames}
      {...styles}
      {...props}
    />
  );
});

Video.displayName = 'Video';

Video.propTypes = {
  id: PropTypes.string.isRequired,
  presets: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  options: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  ratio: PropTypes.number,
  clip: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  size: PropTypes.oneOf(['cover', 'contain', 'fill']),
  readonly: PropTypes.bool,
  autopilot: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object,
  ]),
  className: PropTypes.string,
};

export default Video;
